/** Map a chat-widget GA event onto the analytics allow-list. Unlisted events are dropped. */

export interface ForwardedEvent {
  type: string;
  payload?: Record<string, unknown>;
  target_type?: string;
  target_id?: number;
}

const PASSTHROUGH = new Set([
  "webchat_accepted",
  "lead_captured_sms",
  "lead_captured_webchat",
  "lead_captured_webhook",
  "clicked_powered_by_tomis",
  "chat_conversation_restarted",
]);

const NOT_FORWARDED = new Set([
  "message_received_automated",
  "message_received_support",
  "load_prevented",
  "feedback_resolved",
  "announcement_chat_opened",
]);

export function forwardWidgetEvent(
  eventName: string,
  params: Record<string, unknown> = {},
): ForwardedEvent[] {
  if (NOT_FORWARDED.has(eventName)) return [];
  if (eventName === "loaded") {
    const payload: Record<string, unknown> = {};
    if (typeof params.embed === "boolean") payload.embed = params.embed;
    return [{ type: "widget_loaded", payload }];
  }
  if (eventName === "window_opened" || eventName === "window_closed") {
    const payload: Record<string, unknown> = {};
    if (typeof params.embed === "boolean") payload.embed = params.embed;
    return [{ type: eventName, payload }];
  }
  if (eventName === "message_sent_customer") {
    const payload: Record<string, unknown> = {};
    if (typeof params.quick_reply === "boolean") {
      payload.quick_reply = params.quick_reply;
    }
    return [{ type: eventName, payload }];
  }
  if (PASSTHROUGH.has(eventName)) return [{ type: eventName }];
  if (eventName === "clicked_url") {
    return [{ type: eventName, payload: clickPayload(params, true) }];
  }
  if (eventName === "clicked_phone" || eventName === "clicked_email") {
    return [{ type: eventName, payload: clickPayload(params, false) }];
  }
  if (
    eventName === "feedback_positive" ||
    eventName === "feedback_negative" ||
    eventName === "feedback_message"
  ) {
    return [{ type: eventName, payload: turnPayload(params) }];
  }
  if (eventName === "clicked_quick_reply") {
    return [quickReply(params)];
  }
  if (eventName === "load_failed" || eventName === "crashed") {
    const reason = reasonOf(params);
    return [{ type: eventName, payload: reason ? { reason } : {} }];
  }
  if (eventName === "announcement_impression") {
    return announcementTarget("announcement_shown", params);
  }
  if (eventName === "announcement_click") {
    return announcementTarget("announcement_clicked", params);
  }
  if (eventName === "announcement_dismiss") {
    return announcementTarget("announcement_dismissed", params);
  }
  return [];
}

function clickPayload(
  params: Record<string, unknown>,
  includeUrl: boolean,
): Record<string, unknown> {
  const payload = turnPayload(params);
  if (includeUrl && typeof params.url === "string" && params.url) {
    payload.url = params.url;
  }
  return payload;
}

function turnPayload(params: Record<string, unknown>): Record<string, unknown> {
  if (typeof params.turn_id === "string" && params.turn_id) {
    return { turn_id: params.turn_id };
  }
  return {};
}

function announcementTarget(
  type: string,
  params: Record<string, unknown>,
): ForwardedEvent[] {
  const announcementId = params.announcement_id;

  if (typeof announcementId !== "number") {
    return [];
  }

  return [
    {
      type,
      target_type: "announcement",
      target_id: announcementId,
    },
  ];
}

function quickReply(params: Record<string, unknown>): ForwardedEvent {
  if (typeof params.welcome_button_id === "number") {
    return {
      type: "clicked_quick_reply",
      target_type: "welcome_button",
      target_id: params.welcome_button_id,
    };
  }
  return { type: "clicked_quick_reply" };
}

function reasonOf(params: Record<string, unknown>): string {
  const raw =
    typeof params.reason === "string"
      ? params.reason
      : typeof params.error === "string"
        ? params.error
        : "";
  return raw.slice(0, 100);
}
